import { useEffect, useState } from 'react';
import { ShoppingCart, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

const CAT_KEY = {
  'Meat & Fish': 'cat.meatFish', 'Eggs': 'cat.eggs', 'Dairy': 'cat.dairy',
  'Protein & Supplements': 'cat.protein', 'Grains & Carbohydrates': 'cat.grains',
  'Legumes': 'cat.legumes', 'Nuts & Seeds': 'cat.nuts', 'Oils & Fats': 'cat.oils',
  'Fruit': 'cat.fruit', 'Vegetables': 'cat.vegetables', 'Plant Drinks': 'cat.plantDrinks',
  'Spices & Sauces': 'cat.spices', 'Other': 'cat.other',
};
const CAT_ORDER = ['Meat & Fish', 'Eggs', 'Dairy', 'Protein & Supplements', 'Fish',
  'Grains & Carbohydrates', 'Legumes', 'Vegetables', 'Fruit', 'Nuts & Seeds',
  'Oils & Fats', 'Plant Drinks', 'Spices & Sauces', 'Other'];
const UNIT = {
  de: { g: 'g', ml: 'ml', kg: 'kg', l: 'l', piece: 'Stück', tsp: 'TL', tbsp: 'EL', slice: 'Scheibe', clove: 'Zehe', can: 'Dose', cup: 'Tasse' },
  en: { g: 'g', ml: 'ml', kg: 'kg', l: 'l', piece: 'pcs', tsp: 'tsp', tbsp: 'tbsp', slice: 'slice', clove: 'clove', can: 'can', cup: 'cup' },
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
function fmtQty(q, u, lang) {
  const U = UNIT[lang] || UNIT.de;
  if (u === 'g' && q >= 1000) return `${+(q / 1000).toFixed(2)} kg`;
  if (u === 'ml' && q >= 1000) return `${+(q / 1000).toFixed(2)} l`;
  const n = Math.abs(q - Math.round(q)) < 1e-6 ? Math.round(q) : +q.toFixed(2);
  return `${n} ${U[u] || u}`;
}

export default function ShoppingList({ clientId }) {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('full');
  const [checked, setChecked] = useState({});
  // DE canonical name -> localized display (EN uses the stored *_en, else German).
  const disp = (name, name_en) => cap(lang === 'en' ? (name_en || name) : name);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/nutrition-plans/${clientId}/shopping-list?t=${Date.now()}`);
        setData(res.data);
      } catch { /* silent — section simply not shown */ }
    })();
  }, [clientId]);

  if (!data) return null;
  if (!data.available) {
    return (
      <>
        <p className="as-section mt-8 mb-3 flex items-center gap-2" data-testid="shopping-title"><ShoppingCart size={13} /> {t('nutrition.shopping')}</p>
        <div className="as-card flex items-start gap-2.5 p-4 text-[13px] leading-relaxed text-asgym-sub" data-testid="shopping-unavailable">
          <Info size={15} className="mt-0.5 shrink-0 text-asgym-muted" />
          <span>{t('shop.unavailable')}</span>
        </div>
      </>
    );
  }

  // Flat/legacy or genuine single-week plans: show ONLY "Gesamtplan" (no
  // duplicate "Woche 1" with identical content). Real multi-week plans keep
  // Woche 1..N + Gesamtplan.
  const tabs = data.is_single_week
    ? [{ key: 'full', label: t('shop.fullPlan'), data: data.full }]
    : [...data.weeks.map((w) => ({ key: `w${w.week_number}`, label: `${t('shop.week')} ${w.week_number}`, data: w })),
       { key: 'full', label: t('shop.fullPlan'), data: data.full }];
  const active = (tabs.find((x) => x.key === tab) || tabs[tabs.length - 1]).data;

  const cats = Object.keys(active.categories).sort((a, b) => {
    const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const reviewUnique = dedupeReview(active.review_required);
  const complete = reviewUnique.length === 0;
  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

  return (
    <>
      <p className="as-section mt-8 mb-3 flex items-center gap-2" data-testid="shopping-title"><ShoppingCart size={13} /> {t('nutrition.shopping')}</p>

      {tabs.length > 1 && (
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="shopping-tabs">
          {tabs.map((x) => (
            <button key={x.key} onClick={() => setTab(x.key)} data-testid={`shopping-tab-${x.key}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-[12px] font-bold transition-colors ${tab === x.key ? 'bg-asgym-accent text-black' : 'bg-white/[0.05] text-asgym-sub'}`}>
              {x.label}
            </button>
          ))}
        </div>
      )}

      <div className={`mb-4 flex items-center gap-2 rounded-xl border p-3 text-[13px] font-semibold ${complete ? 'border-asgym-accent/30 bg-asgym-accent/10 text-asgym-accent' : 'border-amber-500/30 bg-amber-500/10 text-amber-400'}`} data-testid="shopping-status">
        {complete ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        {complete ? t('shop.complete') : `${t('shop.incomplete')} · ${reviewUnique.length} ${t('shop.toReview').toLowerCase()}`}
      </div>

      <div className="space-y-5" data-testid="shopping-categories">
        {cats.map((cat) => (
          <div key={cat}>
            <p className="as-label mb-2">{t(CAT_KEY[cat] || 'cat.other')}</p>
            <div className="as-card divide-y divide-white/[0.05]">
              {groupByName(active.categories[cat]).map((g, i) => {
                const id = `${tab}:${cat}:${g.name}`;
                return (
                  <button key={i} onClick={() => toggle(id)} data-testid="shopping-item"
                    className="flex w-full items-center gap-3 p-3.5 text-left">
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${checked[id] ? 'border-asgym-accent bg-asgym-accent' : 'border-white/20'}`}>{checked[id] && <span className="text-[11px] leading-none text-black">✓</span>}</span>
                    <span className={`flex-1 text-[13px] ${checked[id] ? 'text-asgym-muted line-through' : 'text-asgym-text'}`}>{disp(g.name, g.name_en)}</span>
                    <span className={`text-[12px] font-bold ${checked[id] ? 'text-asgym-muted line-through' : 'text-asgym-sub'}`}>{g.parts.map((p) => fmtQty(p.quantity, p.unit, lang)).join(' + ')}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {active.to_taste.length > 0 && (
        <div className="mt-5" data-testid="shopping-astaste">
          <p className="as-label mb-2">{t('shop.asNeeded')}</p>
          <div className="as-card p-3.5 text-[13px] leading-relaxed text-asgym-sub">
            {active.to_taste.map((x) => (typeof x === 'string' ? cap(x) : disp(x.name, x.name_en))).join(' · ')}
          </div>
        </div>
      )}

      {active.review_required.length > 0 && (
        <div className="mt-5" data-testid="shopping-review">
          <p className="as-label mb-2 flex items-center gap-1.5 text-amber-400"><AlertTriangle size={12} /> {t('shop.toReview')}</p>
          <div className="as-card divide-y divide-white/[0.05]">
            {reviewUnique.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 p-3.5">
                <span className="text-[13px] text-asgym-text">{disp(r.ingredient, r.ingredient_en)}</span>
                <span className="text-[11px] font-semibold text-amber-400/80">{t('shop.quantityMissing')}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] leading-snug text-asgym-muted">{t('shop.reviewNote')}</p>
        </div>
      )}

      <p className="mt-4 text-[11px] leading-snug text-asgym-muted">{t('shop.checkHint')}</p>
    </>
  );
}

function dedupeReview(list) {
  const seen = new Set(); const out = [];
  for (const r of list) { const k = (r.ingredient || '').toLowerCase(); if (seen.has(k)) continue; seen.add(k); out.push(r); }
  return out;
}

// Group same-ingredient rows into one line, keeping each unit separate
// (e.g. "Honig — 4 EL + 20 TL"). No unit conversion → no invented quantities.
function groupByName(items) {
  const map = new Map();
  for (const it of items) {
    const g = map.get(it.name) || { name: it.name, name_en: it.name_en, parts: [] };
    g.parts.push({ quantity: it.quantity, unit: it.unit });
    map.set(it.name, g);
  }
  return [...map.values()];
}
